import {
  CalendarBlankIcon,
  CheckCircleIcon,
  GasPumpIcon,
  GearSixIcon,
  LightningIcon,
  MapPinIcon,
  PathIcon,
  ScalesIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatSignedPercent } from '@entities/vehicle/analytics';
import {
  formatMileage,
  formatObservedDate,
  formatPrice,
  fuelLabelKey,
  registrationYear,
  transmissionLabelKey,
} from '@entities/vehicle/lib';
import { Badge, ScoreRing } from '@shared/ui';
import { Media } from '@shared/ui/Media/Media';

import { translatableExplanations } from './explanations';
import {
  confidenceCategory,
  confidenceKey,
  marketDeltaPercent,
  shownMatchCategoryKey,
  shownMatchScore,
} from './lib';
import styles from './ResultCard.module.css';
import type { SearchResultCardData } from './types';
import { useExplanationContext } from './useExplanationContext';

interface ResultCardProps {
  result: SearchResultCardData;
  /** Ссылка ведёт в персональную карточку внутри того же прогона (FE-083). */
  to: string;
  /** Позиция в списке: первые карточки грузят фотографию немедленно. */
  index?: number | undefined;
  /**
   * Сердечко избранного (BE-581, FE-586). Стоит вне ссылки карточки: кнопка
   * внутри `<a>` — вложенный интерактивный элемент.
   */
  favorite?: ReactNode | undefined;
  /** Сообщение о сбое действия на этой карточке. */
  notice?: ReactNode | undefined;
}

/** MVP_PRD §19: на карточке ровно две причины соответствия. */
const CARD_REASONS = 2;

/**
 * Карточка результата подбора (FE-081, MVP_PRD §19).
 *
 * Состав задан продуктом: фотография, название, модификация, цена, год, пробег,
 * топливо, коробка, мощность, местоположение, Match Score, уверенность, доля
 * применённой модели, две причины соответствия, отклонение от медианы и число
 * признаков внимания.
 *
 * Три вещи, которые здесь принципиальны:
 *
 * * **число всегда со словом.** Match Score, уверенность и отклонение от
 *   медианы сопровождаются текстовой категорией: цвет не может быть
 *   единственным носителем смысла;
 * * **«нет данных» не превращается в ноль.** Нет рыночного сравнения — на
 *   карточке так и написано; `attention_count = 0` **не** показывается как
 *   «замечаний нет», такого вывода система делать не вправе (MVP_PRD §15);
 * * **доля применённой модели видна.** `scoring_coverage < 100` означает, что
 *   часть оценки посчитать было нечем, и молчать об этом нельзя (MATCHING §8.3).
 *
 * Сердечко избранного приходит слотом (`favorite`): мутация живёт в фиче, а
 * карточка — сущность. Сравнения отдельным набором нет (CLAUDE.md §2).
 */
export function ResultCard({ result, to, index = 0, favorite, notice }: ResultCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const explanations = useExplanationContext(result.price.currency);

  const noData = t('catalog.noData');
  const { match } = result;
  const year = registrationYear(result.first_registration_date);
  const observedAt = formatObservedDate(result.data_observed_at, locale);
  const deltaPercent = marketDeltaPercent(result.market);
  const category = match.match_confidence_category || confidenceCategory(match.match_confidence);

  const reasons = translatableExplanations(match.top_reasons, explanations).slice(0, CARD_REASONS);
  const imageAlt = result.cover_image?.alt ?? `${result.make} ${result.model}`;

  return (
    <article className={[styles.card, favorite && styles.withFavorite].filter(Boolean).join(' ')}>
      <Link to={to} className={styles.link}>
        <div className={styles.mediaWrap}>
          <Media
            src={result.cover_image?.card_url ?? undefined}
            alt={imageAlt}
            ratio="4 / 3"
            priority={index < 2}
            sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
          />
          <span className={styles.rank}>
            <span className="visually-hidden">{t('results.card.rank')}</span>
            <span className="tabular">{result.rank}</span>
          </span>
          {result.is_new ? (
            <span className={styles.badgeSlot}>
              <Badge tone="neutral">
                {t('catalog.new')}
                <span className="visually-hidden"> — {t('catalog.newHint')}</span>
              </Badge>
            </span>
          ) : null}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <div className={styles.identity}>
              <h3 className={styles.name}>
                {result.make} {result.model}
              </h3>
              <p className={styles.variant}>{result.variant ?? noData}</p>
            </div>
            <ScoreRing
              score={shownMatchScore(match.score)}
              size={68}
              title={t('results.card.matchTitle')}
              label={t(shownMatchCategoryKey(match.score))}
            />
          </div>

          <dl className={styles.specs}>
            <div>
              <dt>
                <CalendarBlankIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.year')}</span>
              </dt>
              <dd className="tabular">{year ?? noData}</dd>
            </div>
            <div>
              <dt>
                <PathIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.mileage')}</span>
              </dt>
              <dd className="tabular">
                {t('catalog.spec.mileageValue', {
                  value: formatMileage(result.mileage_km, locale),
                })}
              </dd>
            </div>
            <div>
              <dt>
                <GasPumpIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.fuel')}</span>
              </dt>
              <dd>{t(fuelLabelKey(result.fuel_type))}</dd>
            </div>
            <div>
              <dt>
                <GearSixIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.transmission')}</span>
              </dt>
              <dd>{t(transmissionLabelKey(result.transmission))}</dd>
            </div>
            <div>
              <dt>
                <LightningIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('vehicle.specs.power')}</span>
              </dt>
              <dd className="tabular">
                {result.power_kw === null
                  ? noData
                  : t('vehicle.specs.powerValue', { kw: result.power_kw })}
              </dd>
            </div>
            <div>
              <dt>
                <MapPinIcon size={16} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.location')}</span>
              </dt>
              <dd>{result.location.city ?? t('catalog.noData')}</dd>
            </div>
          </dl>

          {reasons.length > 0 ? (
            <ul className={styles.reasons}>
              {reasons.map(({ item, text }) => (
                <li key={item.code}>
                  <CheckCircleIcon size={16} weight="fill" aria-hidden="true" />
                  {text}
                </li>
              ))}
            </ul>
          ) : null}

          <div className={styles.signals}>
            {/* Уверенность в персональном соответствии — не вероятность удачной
                покупки (MVP_PRD §17), поэтому подписана словом «уверенность». */}
            <span className={styles.signal}>
              <span className={styles.signalLabel}>{t('results.card.confidence')}</span>
              <span className={`${styles.signalValue} tabular`}>
                {Math.round(match.match_confidence)}
              </span>
              <span className={styles.signalNote}>{t(confidenceKey(category))}</span>
            </span>

            {deltaPercent === null ? (
              <span className={`${styles.signal} ${styles.signalMuted}`}>
                <ScalesIcon size={15} aria-hidden="true" />
                {t('results.card.marketUnavailable')}
              </span>
            ) : (
              <span className={`${styles.signal} ${styles.signalInfo}`}>
                <ScalesIcon size={15} aria-hidden="true" />
                {t('results.card.marketDelta', {
                  value: formatSignedPercent(deltaPercent, locale),
                })}
              </span>
            )}

            {result.attention_count > 0 ? (
              <span className={`${styles.signal} ${styles.signalAttention}`}>
                <WarningIcon size={15} weight="fill" aria-hidden="true" />
                {t('vehicle.attention.count', { count: result.attention_count })}
              </span>
            ) : null}
          </div>

          {/* Доля применённой модели: «плохое объявление» и «нечем оценить
              сценарий» не должны маскировать друг друга (MATCHING §8.4). */}
          {match.scoring_coverage < 100 ? (
            <p className={styles.coverage}>
              {t('results.card.coverage', { percent: Math.round(match.scoring_coverage) })}
            </p>
          ) : null}

          <div className={styles.footer}>
            <p className={`${styles.price} tabular`}>{formatPrice(result.price, locale)}</p>
            <p className={styles.observed}>
              {observedAt === null ? noData : t('catalog.spec.dataDate', { date: observedAt })}
            </p>
          </div>
        </div>
      </Link>
      {favorite ? <div className={styles.favoriteSlot}>{favorite}</div> : null}
      {notice ? <div className={styles.notice}>{notice}</div> : null}
    </article>
  );
}
